import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';

import { familyAdapters } from './familyAdapters';
import { toId } from './groupIds';
import { isDefaultGroup } from './groupRules';

import type {
  AdGroupRefs,
  DeletedGroup,
  DeletionMode,
  GroupableItem,
  GroupChangeContext,
  GroupChangeOutcome,
  GroupFamily,
  GroupingState,
  GroupLike,
  GroupReferences,
  TemporalPacingGroupRef,
  TemporalPacingLike
} from './grouping.types';

export interface DeletionModeInput {
  existedOnLoad: boolean;
  family: GroupFamily;
  isDraft: boolean;
}

/**
 * Hard delete (drop from the array) when the group never reached the backend or the entity is draft;
 * soft delete (`deleted: true`) otherwise. Venue groups live on the user, so draft status is irrelevant.
 */
export function resolveDeletionMode({ existedOnLoad, family, isDraft }: DeletionModeInput): DeletionMode {
  if (!existedOnLoad) {
    return 'hard';
  }
  return isDraft && !familyAdapters[family].userLevel ? 'hard' : 'soft';
}

/** Returns the item with a new `groups` list; the only place item group ids are written. */
export function withGroups<I extends GroupableItem>(item: I, groups: string[]): I {
  return { ...item, groups };
}

export interface DetachInput<I extends GroupableItem> {
  family: GroupFamily;
  groupIds: string[];
  /** Groups after the change, used to find the screens default group and active remaining groups. */
  groups: GroupLike[];
  /** Restrict to these item ids; every item when omitted. */
  itemIds?: string[];
  items: I[];
}

/** Removes group ids from items; screens left without any active group fall back to the default group. */
export function detachGroupsFromItems<I extends GroupableItem>({
  family,
  groupIds,
  groups,
  itemIds,
  items
}: DetachInput<I>): { affectedItemIds: string[]; items: I[] } {
  const adapter = familyAdapters[family];
  const targets = new Set(groupIds);
  const scope = itemIds ? new Set(itemIds) : null;
  const activeGroupIds = new Set(groups.filter(isActiveGroup).map(group => toId(group._id)));
  const defaultGroupId = adapter.defaultGroupId(groups);
  const affectedItemIds: string[] = [];

  const nextItems = items.map(item => {
    const id = adapter.itemId(item);
    const current = (item.groups ?? []).map(toId);
    if ((scope && !scope.has(id)) || !current.some(groupId => targets.has(groupId))) {
      return item;
    }
    const remaining = current.filter(groupId => !targets.has(groupId));
    const needsDefault = defaultGroupId !== undefined && !remaining.some(groupId => activeGroupIds.has(groupId));
    affectedItemIds.push(id);
    return withGroups(item, needsDefault ? [...remaining, defaultGroupId] : remaining);
  });

  return { affectedItemIds, items: nextItems };
}

const refsInclude = (refs: unknown[] | undefined, targets: Set<string>): boolean =>
  (refs ?? []).some(ref => targets.has(toId(ref)));

const tpRefsInclude = (refs: TemporalPacingGroupRef[] | undefined, targets: Set<string>): boolean =>
  (refs ?? []).some(ref => targets.has(toId(ref.id)));

/** Number of active ads and temporal pacing groups referencing any of the given groups. */
export function countGroupReferences(state: GroupingState, family: GroupFamily, groupIds: string[]): GroupReferences {
  const key = familyAdapters[family].adGroupKey;
  if (!key) {
    return { ads: 0, temporalPacing: 0 };
  }
  const targets = new Set(groupIds);
  const ads = state.ads.filter(ad => !ad.deleted && refsInclude(ad[key], targets)).length;
  const temporalPacing = (state.temporalPacing?.groups ?? []).filter(
    group => !group.deleted && tpRefsInclude(group[key], targets)
  ).length;
  return { ads, temporalPacing };
}

/** Removes the group ids from every ad and temporal pacing group (immutable; untouched entries are reused). */
export function removeGroupReferences<
  G extends GroupLike,
  I extends GroupableItem,
  A extends AdGroupRefs,
  T extends TemporalPacingLike
>(state: GroupingState<G, I, A, T>, family: GroupFamily, groupIds: string[]): GroupingState<G, I, A, T> {
  const key = familyAdapters[family].adGroupKey;
  if (!key) {
    return state;
  }
  const targets = new Set(groupIds);
  const ads = state.ads.map(ad =>
    refsInclude(ad[key], targets) ? { ...ad, [key]: (ad[key] ?? []).filter(ref => !targets.has(toId(ref))) } : ad
  );
  const referencesTemporalPacing = (state.temporalPacing?.groups ?? []).some(group =>
    tpRefsInclude(group[key], targets)
  );
  const temporalPacing =
    state.temporalPacing && referencesTemporalPacing
      ? {
          ...state.temporalPacing,
          groups: state.temporalPacing.groups.map(group =>
            tpRefsInclude(group[key], targets)
              ? { ...group, [key]: (group[key] ?? []).filter(ref => !targets.has(toId(ref.id))) }
              : group
          )
        }
      : state.temporalPacing;
  return { ...state, ads, temporalPacing };
}

/**
 * Deletes active groups: hard or soft per {@link resolveDeletionMode}, detaches their members and
 * cleans ads / temporal pacing. `references` reports what the input state referenced, for the prompt.
 * The screens default group is never deleted.
 */
export function deleteGroups<
  G extends GroupLike,
  I extends GroupableItem,
  A extends AdGroupRefs,
  T extends TemporalPacingLike
>(
  state: GroupingState<G, I, A, T>,
  groupIds: string[],
  context: GroupChangeContext<G>
): GroupChangeOutcome<G, I, A, T> {
  const { existedOnLoad, family, isDraft } = context;
  const deletedGroups: DeletedGroup[] = state.groups
    .filter(group => isActiveGroup(group) && !isDefaultGroup(family, group) && groupIds.includes(toId(group._id)))
    .map(group => {
      const id = toId(group._id);
      return {
        id,
        mode: resolveDeletionMode({ existedOnLoad: existedOnLoad.has(id), family, isDraft }),
        name: group.name
      };
    });
  if (!deletedGroups.length) {
    return { affectedItemIds: [], deletedGroups, references: { ads: 0, temporalPacing: 0 }, state };
  }

  const modeById = new Map(deletedGroups.map(({ id, mode }) => [id, mode]));
  const ids = [...modeById.keys()];
  const groups = state.groups.flatMap(group => {
    const mode = modeById.get(toId(group._id));
    if (!mode) {
      return [group];
    }
    return mode === 'hard' ? [] : [{ ...group, deleted: true }];
  });
  const detached = detachGroupsFromItems({ family, groupIds: ids, groups, items: state.items });

  return {
    affectedItemIds: detached.affectedItemIds,
    deletedGroups,
    references: countGroupReferences(state, family, ids),
    state: removeGroupReferences({ ...state, groups, items: detached.items }, family, ids)
  };
}
